import React, { useState, useEffect } from "react";
import { X, Info } from "lucide-react";
import { UpdateTraderOffersData } from "../../../../Rest_api/RestApi";
import TimeInput from "../../../../components/TimeInput/TimeInput";
import { useSelector } from "react-redux";

interface TimeSlot {
  id: string;
  start_slot: string;
  end_slot: string;
  quantum: string;
  rate: string;
  label?: string;
}

interface OfferRow {
  id: string;
  startDate: string;
  endDate: string;
  type: "RTC" | "PEAK" | "CUSTOM";
  timeSlots: TimeSlot[];
  originalId?: number;
}

interface EditOfferDataModalProps {
  isModalOpen: boolean;
  setIsModalOpen: (open: boolean) => void;
  offerData: OfferRow;
  onUpdate: () => void;
}



export const EditOfferDataModal: React.FC<EditOfferDataModalProps> = ({
  isModalOpen,
  setIsModalOpen,
  offerData,
  onUpdate,
}) => {
  const SelectedStartDate = useSelector(
    (state: any) => state.ProcurementOptimizerGridcoDate.startDate
  );
  const SelectedEndDate = useSelector(
    (state: any) => state.ProcurementOptimizerGridcoDate.endDate
  );

  const [offer, setOffer] = useState<OfferRow>(offerData);
  const [loading, setLoading] = useState(false);


  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showErrors, setShowErrors] = useState(false);

  // Update offer when offerData changes
  useEffect(() => {
    if (offerData) {
      setOffer(offerData);
      setOffer(offerData);
    }
  }, [offerData]);

  const validateOffer = () => {
    const newErrors: Record<string, string> = {};

    if (!offer.startDate) {
      newErrors[`${offer.id}-startDate`] = "Required";
    }
    if (!offer.endDate) {
      newErrors[`${offer.id}-endDate`] = "Required";
    }

    offer.timeSlots.forEach((slot) => {
      if (offer.type !== "RTC") {
        if (!slot.start_slot) {
          newErrors[`${offer.id}-${slot.id}-start_slot`] = "Required";
        }
        if (!slot.end_slot) {
          newErrors[`${offer.id}-${slot.id}-end_slot`] = "Required";
        }
      }

      if (!slot.quantum) {
        newErrors[`${offer.id}-${slot.id}-quantum`] = "Required";
      }
      if (!slot.rate) {
        newErrors[`${offer.id}-${slot.id}-rate`] = "Required";
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleUpdateOffer = (field: string, value: any) => {
    setOffer((prev) => {
      // If updating start date and end date is before it, clear end date
      if (field === "startDate" && prev.endDate && value > prev.endDate) {
        return { ...prev, [field]: value, endDate: "" };
      }
      return { ...prev, [field]: value };
    });
    if (errors[`${offer.id}-${field}`]) {
      const newErrors = { ...errors };
      delete newErrors[`${offer.id}-${field}`];
      setErrors(newErrors);
    }
  };

  const handleUpdateTimeSlot = (slotId: string, field: string, value: any) => {
    setOffer((prev) => ({
      ...prev,
      timeSlots: prev.timeSlots.map((slot) =>
        slot.id === slotId ? { ...slot, [field]: value } : slot
      ),
    }));
    const errorKey = `${offer.id}-${slotId}-${field}`;
    if (errors[errorKey]) {
      const newErrors = { ...errors };
      delete newErrors[errorKey];
      setErrors(newErrors);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setShowErrors(true);
    if (validateOffer()) {
      try {
        // Transform data for API
        const apiData = {
          id: offer.originalId, // Include the original ID from the backend
          type: offer.type,
          start_date: offer.startDate,
          end_date: offer.endDate,
          trader_offer: offer.timeSlots.map(
            ({ id, label, quantum, rate, ...slot }) => ({
              ...slot,
              start_slot: slot.start_slot,
              end_slot: slot.end_slot,
              quantum: quantum, // Use the same key as backend
              rate: rate, // Use the same key as backend
            })
          ),
        };
        // Call API to update the offer
        await UpdateTraderOffersData(apiData);

        // Notify parent component
        onUpdate();
        setShowErrors(false);
      } catch (error) {
        console.error("Error updating offer:", error);
        alert("Failed to update offer. Please try again.");
      } finally {
        setLoading(false);
        setIsModalOpen(false);
      }
    }
  };

  const handleClose = () => {
    setIsModalOpen(false);
    setShowErrors(false);
    // Reset errors on close
    setErrors({});
  };

  if (!isModalOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[99] p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between bg-white">
          <h2 className="text-xl font-medium text-[#525062]">Edit Offer</h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Close modal"
          >
            <X strokeWidth={3} size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-6 bg-[#F3F5F8]">
          {showErrors && Object.keys(errors).length > 0 && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm font-medium">
                Please fill all required fields
              </p>
            </div>
          )}

          <div className="bg-white rounded-lg border border-gray-400 p-6">
            {/* First Row: Type, Start Date, End Date */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium mb-2 text-[#6F6D7C]">
                  Type
                </label>
                <div className="w-full px-3 py-2 h-[45px] border border-[#E2E2E5] rounded-lg bg-gray-100 cursor-not-allowed text-gray-700 flex items-center">
                  <span className="text-base font-medium text-orange-500">
                    {offer.type}
                  </span>
                </div>
              </div>

              <div>
                <label className="flex items-center gap-1 text-sm font-medium mb-2 text-[#6F6D7C]">
                  Start Date
                  {/* <Info size={14} className="text-gray-400" /> */}
                </label>
                <input
                  type="date"
                  value={offer.startDate}
                  onChange={(e) =>
                    handleUpdateOffer("startDate", e.target.value)
                  }
                  min={SelectedStartDate}
                  max={SelectedEndDate}
                  className={`w-full px-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white text-[#6F6D7C] font-medium ${
                    showErrors && errors[`${offer.id}-startDate`]
                      ? "border-red-500"
                      : "border-[#E2E2E5]"
                  }`}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2 text-[#6F6D7C]">
                  End Date
                </label>
                <input
                  type="date"
                  value={offer.endDate}
                  onChange={(e) => handleUpdateOffer("endDate", e.target.value)}
                  min={offer.startDate ? offer.startDate : SelectedStartDate}
                  max={SelectedEndDate}
                  disabled={!offer.startDate}
                  className={`w-full px-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-[#6F6D7C] font-medium ${
                    !offer.startDate
                      ? "bg-gray-100 cursor-not-allowed"
                      : "bg-white"
                  } ${
                    showErrors && errors[`${offer.id}-endDate`]
                      ? "border-red-500"
                      : "border-[#E2E2E5]"
                  }`}
                />
              </div>
            </div>

            {/* Time Slots */}
            <div className="space-y-4">
              {offer.timeSlots.map((slot, slotIndex) => {
                const slotKey = `${offer.id}-${slot.id}`;




                return (
                  <div key={slot.id}>
                    {/* Label for Peak type */}
                    {offer.type === "PEAK" && slot.label && (
                      <div className="text-sm font-medium mb-2 text-[#FF8C42]">
                        {slot.label}
                      </div>
                    )}
                    {offer.type === "CUSTOM" && (
                      <div className="text-sm font-medium mb-2 text-gray-700">
                        Slot {slotIndex + 1}
                      </div>
                    )}

                    <div className="grid grid-cols-4 gap-4 items-start">
                      <div>
                        <label className="flex items-center gap-1 text-sm font-medium mb-2 text-[#6F6D7C]">
                          Start Time
                          {/* <Info size={14} className="text-gray-400" /> */}
                        </label>
                        {offer.type === "RTC" ? (
                          <div className="w-full px-3 py-2.5 border border-[#E2E2E5] rounded-lg bg-gray-100 cursor-not-allowed text-gray-700 flex items-center h-[40px]">
                            <span className="text-sm text-[#6F6D7C] font-medium">
                              00:00
                            </span>
                          </div>
                        ) : (
                          <div className={`w-full h-[44px] px-2 py-1 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white text-gray-700 `}>
                            <TimeInput
                              onChange={(val) =>
                                handleUpdateTimeSlot(slot.id, "start_slot", val)
                              }
                              height={32}
                              type="startTime"
                              value={slot.start_slot}
                              heading={""}
                            />
                          </div>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2 text-[#6F6D7C]">
                          End Time
                        </label>
                        {offer.type === "RTC" ? (
                          <div className="w-full px-3 py-2.5 border border-[#E2E2E5] rounded-lg bg-gray-100 cursor-not-allowed text-gray-700 flex items-center h-[40px]">
                            <span className="text-sm text-[#6F6D7C] font-medium">
                              24:00
                            </span>
                          </div>
                        ) : (
                          <div className={`w-full h-[44px] px-2 py-1 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white text-[#6F6D7C] font-medium `}>
                            <TimeInput
                              onChange={(val) =>
                                handleUpdateTimeSlot(slot.id, "end_slot", val)
                              }
                              height={32}
                              type="endTime"
                              value={slot.end_slot}
                              heading={""}
                              minTime={slot.start_slot}
                            />
                          </div>
                        )}
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2 text-[#6F6D7C]">
                          Quantum (MW)
                        </label>
                        <input
                          type="text"
                          value={slot.quantum}
                          onChange={(e) =>
                            handleUpdateTimeSlot(
                              slot.id,
                              "quantum",
                              e.target.value
                            )
                          }
                          className={`w-full px-2 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white text-[#6F6D7C] font-medium ${
                            showErrors &&
                            errors[`${offer.id}-${slot.id}-quantum`]
                              ? "border-red-500"
                              : "border-[#E2E2E5]"
                          }`}
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-2 text-[#6F6D7C]">
                          Rate (₹/kWh)
                        </label>
                        <input
                          type="text"
                          value={slot.rate}
                          onChange={(e) =>
                            handleUpdateTimeSlot(
                              slot.id,
                              "rate",
                              e.target.value
                            )
                          }
                          className={`w-full px-2 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 bg-white text-[#6F6D7C] font-medium ${
                            showErrors && errors[`${offer.id}-${slot.id}-rate`]
                              ? "border-red-500"
                              : "border-[#E2E2E5]"
                          }`}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-center gap-3 bg-white">
          <button
            onClick={handleClose}
            className="flex items-center justify-center px-10 py-2.5 border border-[#8C8A96] text-[#8C8A96] text-xl rounded-xl font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="flex items-center justify-center px-10 py-2.5 bg-[#F1935C] text-white text-xl rounded-xl font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
          >
            {loading ? "Loading..." : "Update"}
          </button>
        </div>
      </div>
    </div>
  );
};
